import { Formik } from 'formik'
import React from 'react'

import { Dashboard, PageLayout } from '@components/common'
import { SideSummary } from '@components/common/SideSummary'
import { DashboardBackButton } from '@components/utils'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import { HeadContent, Loading, SaveShortcutListener } from '@fourthwall/components'
import type { ShopsApiClient } from '@fourthwall/services/dist/ShopsApiClient'
import { getCountryNameByCode, getStateNameByCode } from '@fourthwall/utils/dist/inputs'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'
import { routing } from '@utils/routing'

import type { FormValues } from './ShippingGroup.types'
import { Countries } from './components/Countries/Countries'
import { Products } from './components/Products/Products'
import { validationSchema } from './consts'

type ShippingLocation = Awaited<
  ReturnType<typeof ShopsApiClient.getCurrentShop>
>['data']['originAddress']

export type ShippingGroupViewProps = {
  isLoading: boolean
  isSelfFulfilled: boolean
  initialValues: FormValues
  shippingLocation: ShippingLocation
  onSubmit: (formValues: FormValues) => Promise<void>
}

export const ShippingGroupView = ({
  isLoading,
  initialValues,
  isSelfFulfilled,
  shippingLocation,
  onSubmit,
}: ShippingGroupViewProps) => {
  const title = `Custom shipping group for products ${isSelfFulfilled ? 'you ship' : 'we ship for you'}`

  const renderState = () => {
    if (shippingLocation?.state && shippingLocation?.country) {
      return getStateNameByCode(shippingLocation?.country, shippingLocation.state)
    }

    return shippingLocation?.state
  }

  const renderCountry = () => {
    if (shippingLocation?.country) {
      return getCountryNameByCode(shippingLocation?.country)
    }

    return shippingLocation?.country
  }

  const renderShippingLocation = () => {
    if (isSelfFulfilled) {
      return shippingLocation ? (
        <>
          {shippingLocation.address1} <br />
          {shippingLocation.address2 && (
            <>
              {shippingLocation.address2}
              <br />
            </>
          )}
          {renderState()} {shippingLocation.zip} {shippingLocation.city} <br />
          {renderCountry()}
        </>
      ) : (
        '-'
      )
    }

    return 'Fulfilled by Fourthwall'
  }

  const getBackToShippingLink = () => {
    const searchParams = new URLSearchParams(location.search)
    searchParams.delete('selfFulfilled')

    return {
      pathname: routing.settings.shipping.self,
      search: searchParams.toString(),
    }
  }

  return (
    <>
      <HeadContent title={title} />
      <Formik<FormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={onSubmit}
      >
        {({ dirty, isValid, isSubmitting, submitForm, resetForm }) => (
          <>
            <PageLayout
              size="medium"
              leftContent={
                <Loading isLoading={isLoading}>
                  <Products isSelfFulfilled={isSelfFulfilled} />
                  <Countries />
                </Loading>
              }
              rightContent={
                shippingLocation && (
                  <SideSummary>
                    <SideSummary.Item title="Shipping from">
                      {renderShippingLocation()}
                    </SideSummary.Item>
                  </SideSummary>
                )
              }
            >
              <Dashboard.ItemViewHeader
                name={title}
                backButton={<DashboardBackButton label="Shipping" to={getBackToShippingLink()} />}
              />
            </PageLayout>
            <SaveShortcutListener disabled={!dirty} onSave={submitForm} />
            <ConfirmBar
              isOpen={dirty}
              isLoading={isSubmitting}
              onConfirm={submitForm}
              onCancel={resetForm}
            />
            <LeavingGuard when={dirty} isValid={isValid} onSave={submitForm} />
          </>
        )}
      </Formik>
    </>
  )
}
